import { Controller } from "@hotwired/stimulus"
import { resolveCaptureTarget } from "../lib/pdf_export_helpers.mjs"

// Connects to data-controller="pdf-export"
//
// Thin controller for the provider-search "View results" and provider-details
// download buttons. The heavy PDF machinery (html2canvas + pdf-lib) lives in a
// separate on-demand bundle (app/javascript/pdf_export.js) that this controller
// injects the first time a user clicks a download control, so those libraries
// stay out of the global application.js payload.
//
// Values:
//   - filename:        the exact download filename (built server-side)
//   - captureSelector: optional CSS selector for the element to capture.
//                      Defaults to the enclosing drawer content
//                      ([data-drawer-layout]).
//   - heading:         optional document title drawn at the top of the PDF.
//   - subheading:      optional secondary line drawn under the heading.
//   - scriptUrl:       digested asset URL of the on-demand export bundle.

// Shared across every controller instance so multiple buttons trigger a single
// download of the export bundle.
let libraryPromise = null

export default class extends Controller {
  static values = {
    filename: { type: String, default: "export.pdf" },
    captureSelector: { type: String, default: "" },
    heading: { type: String, default: "" },
    subheading: { type: String, default: "" },
    scriptUrl: { type: String, default: "" }
  }

  async export(event) {
    if (event) event.preventDefault()
    // Re-entrancy guard: ignore repeat clicks / Enter / Space while a capture is
    // already running so we never start concurrent full-drawer jobs.
    if (this.busy) return

    const target = this.captureTarget
    if (!target) {
      console.error("[pdf-export] No capture target found")
      return
    }

    const button = event && event.currentTarget
    this.busy = true
    this.setLoading(button, true)

    try {
      const lib = await this.loadLibrary()
      await lib.generate({
        target,
        filename: this.filenameValue,
        heading: this.headingValue,
        subheading: this.subheadingValue
      })
    } catch (error) {
      console.error("[pdf-export] Failed to generate PDF:", error)
    } finally {
      this.busy = false
      this.setLoading(button, false)
    }
  }

  get captureTarget() {
    return resolveCaptureTarget(this.element, this.captureSelectorValue, document)
  }

  // Load the on-demand export bundle once and resolve to window.GBPdfExport.
  loadLibrary() {
    if (window.GBPdfExport) return Promise.resolve(window.GBPdfExport)
    if (!this.scriptUrlValue) {
      return Promise.reject(new Error("[pdf-export] Missing export bundle URL"))
    }
    if (!libraryPromise) {
      libraryPromise = new Promise((resolve, reject) => {
        const script = document.createElement("script")
        script.src = this.scriptUrlValue
        script.async = true
        script.addEventListener("load", () => {
          if (window.GBPdfExport) {
            resolve(window.GBPdfExport)
          } else {
            libraryPromise = null // allow a retry on the next click
            reject(new Error("[pdf-export] Export bundle loaded but global missing"))
          }
        })
        script.addEventListener("error", () => {
          libraryPromise = null // allow a retry on the next click
          reject(new Error("[pdf-export] Failed to load export bundle"))
        })
        document.head.appendChild(script)
      })
    }
    return libraryPromise
  }

  setLoading(button, loading) {
    if (!button) return
    button.classList.toggle("opacity-50", loading)
    button.classList.toggle("pointer-events-none", loading)
    button.setAttribute("aria-busy", loading ? "true" : "false")
    button.setAttribute("aria-disabled", loading ? "true" : "false")
  }
}
